import * as React from 'react'
import { useSelector } from 'react-redux'
import { Layout as MuiLayout, LayoutProps, useTranslate, MenuItemLink, Sidebar, useSidebarState } from 'react-admin'
import classnames from 'classnames'
import { usePermissions } from '@hooks/useHasPermission'
import AppBarExtend from './AppBarExtend'
import Menu from './Menu'
import AppErrorBoundary from './AppErrorBoundary'
import { BackToHomeProvider } from './BackToHomeButton'
import ImpersonationBanner from '@component/ImpersonationBanner'
import { makeStyles } from '@mui/styles'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { useLocation } from 'react-router'
import { useMemo } from 'react'
import usePermissionRefresh from '@hooks/usePermissionRefresh'
import { useBuildingBootstrap } from '@hooks/useBuildingBootstrap'

import { useIsOneClick } from '@/dashboard/dashboardTheme'

const EmptyMenu = () => <div></div>
const EmptyAppBar = () => null
const EmptySidebar = () => null

const AddMenu = () => {
  const classes = useStyles()
  const translate = useTranslate()
  return (
    <div>
      <MenuItemLink
        className={classnames(classes.menulink)}
        to={{
          pathname: '/accountCreate',
        }}
        state={{ _scrollToTop: true }}
        primaryText={translate('pos.menu.create', {
          smart_count: 2,
        })}
        leftIcon={<PersonAddIcon />}
        dense={false}
      />
    </div>
  )
}

export const useStyles = makeStyles((theme: any) => ({
  layout: {
    // Header height as a CSS variable, so every offset that assumes it (the app-frame
    // margin, sidebar heights, sticky sub-headers, the dashboards' viewport maths) follows
    // one number. 72px, down from the 120px bar that took a quarter of a zoomed laptop
    // screen (3472); the 48px scope dropdowns, the 36px theme toggle and the 32px avatar
    // all sit inside it. The logo scales with it.
    '--app-bar-h': '72px',
    '--app-bar-logo-h': '40px',
    // The shell never grows past the viewport (DESIGN_GUIDE.md 9.3, specs#3497).
    // react-admin's own Layout root is `min-width: fit-content` and its content box keeps
    // the flex default `min-width: auto`, so anything with an intrinsic width — a KPI row
    // whose split cards cannot shrink, a wide table — pushed the whole page wider than the
    // window: the page scrolled sideways, and a framed table's right-hand arrows sat
    // off-screen until it did. Both floors go; wide content now overflows (or scrolls)
    // inside its own box, which is what the kit's frames and the list pages' `width: 0`
    // trick below already expect.
    minWidth: 0,
    '& .RaLayout-appFrame': {
      marginTop: 'var(--app-bar-h, 72px)',
    },
    '& .RaLayout-content': {
      padding: 0,
      minWidth: 0,
    },
    '& .RaList-root': {
      backgroundColor: 'red',
    },
    '& .RaBulkActionsToolbar-toolbar': {
      display: 'none',
    },
    '& .RaList-main': {
      width: 0,
      backgroundColor: 'white',
      borderRadius: 8,
      padding: 24,
      margin: '0 0 8px',
      '& .MuiToolbar-root': {
        minHeight: 'auto',
      },
    },
    '& .RaList-content': {
      borderRadius: 0,
      boxShadow: 'none',
    },
    '& .RaDatagrid-thead': {
      border: '1px solid #F5F5F5',
    },
    '& .RaDatagrid-tableWrapper': {
      border: '1px solid #F5F5F5',
      borderRadius: 8,
      overflowX: 'auto',
      boxShadow: 'inset -8px 0 8px -8px rgba(0, 0, 0, 0.25)',
    },
    '& .RaDatagrid-table': {
      boxShadow: 'inset -8px 0 0 #fff',
    },
    '& .RaDatagrid-tbody': {
      '& .RaDatagrid-row': {
        // borderBottom: '1px solid #ECECEC',
        '&:last-child': {
          borderBottom: 0,
        },
      },
    },
    '& .RaDatagrid-headerCell': {
      fontWeight: 700,
      fontSize: 16,
      fontFamily: 'Roboto',
      color: '#211F1F',
      backgroundColor: '#E4E1EE',
      border: 'none',
      height: 48,
      padding: '12px 16px',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
    },
    '& .RaDatagrid-rowCell': {
      padding: '12px 16px',
    },
    '& .RaDatagrid-hover': {
      background: '#F5F5F5 !important',
    },
    '& .RaDatagrid-row': {
      borderBottom: '1px solid #F5F5F5',
    },
  },
  layoutNoChrome: {
    '& .RaLayout-appFrame': {
      marginTop: 0,
    },
  },
  menulink: {
    width: '200px',
  },
  slide: {
    height: 'calc(100vh - var(--app-bar-h, 72px))',
    '& .RaSidebar-appBarCollapsed': {
      marginTop: 0,
    },
    // 232 (was 273): the nav's long labels already wrap to two lines, so the width
    // bought little; the content area gets the 41px back (3472).
    '& .RaSidebar-paper': {
      width: 232,
      padding: '0 12px 0',
    },
    '& .RaSidebar-fixed': {
      height: 'calc(100vh - var(--app-bar-h, 72px))',
      // The menu keeps its full width in both states; this box is what
      // clips it, so the labels slide sideways behind the icon column.
      width: 208,
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },
  // Collapsed: only the pinned icon column (toggle + top-level group icons)
  // stays visible. Same horizontal padding as open so icons never move.
  slideClosed: {
    '& .RaSidebar-paper': {
      width: 72,
    },
    '& .RaSidebar-fixed': {
      width: 48,
    },
  },
  // Sits in the icon column above the groups, styled like a menu row so it
  // reads as part of the rail rather than a floating control.
  sidebarToggle: {
    display: 'flex',
    alignItems: 'center',
    height: 30,
    marginTop: 8,
    paddingLeft: 16,
  },
  sidebarToggleButton: {
    color: '#211F1F',
    width: 24,
    height: 24,
    padding: 0,
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
}))
// @ts-ignore
const CustomSidebar = (props) => {
  const location = useLocation()
  const classes = useStyles()
  const hiddenBar = ['/surveyWelcome', '/multipleContentSurvey'].some((v) => location.pathname.includes(v))
  const { hiddenMenu } = useMemo(() => location.state || {}, [location.state])
  // react-admin persists this in the store (localStorage), so the choice
  // survives reloads. Default is open on desktop.
  const [open, setOpen] = useSidebarState()

  if (hiddenBar || hiddenMenu) {
    return null
  }
  return (
    <Sidebar {...props} className={classnames(classes.slide, !open && classes.slideClosed)}>
      <div className={classes.sidebarToggle}>
        <Tooltip title={open ? 'Collapse menu' : 'Expand menu'} placement="right">
          <IconButton
            size="small"
            className={classes.sidebarToggleButton}
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Collapse menu' : 'Expand menu'}
            aria-expanded={open}
          >
            {open ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </div>
      {props.children}
    </Sidebar>
  )
}

const Layout = (props: LayoutProps) => {
  const classes = useStyles()
  const location = useLocation()
  const oneClick = useIsOneClick()
  usePermissionRefresh()
  useBuildingBootstrap()

  const hideChrome = oneClick && location.pathname === '/'

  const { selectedGroupId } = useSelector((state: any) => state.profile)
  const permissions = usePermissions()
  const { GlobalManageCustomers } = permissions || {}
  const myMenu = oneClick
    ? EmptyMenu
    : location.pathname === '/accountCreate'
    ? AddMenu
    : GlobalManageCustomers && !selectedGroupId
    ? EmptyMenu
    : Menu
  return (
    <>
      <ImpersonationBanner />
      <MuiLayout
        {...props}
        appBar={hideChrome ? EmptyAppBar : AppBarExtend}
        menu={myMenu}
        // One-click nav has no category sidenav, so the rail is dropped
        // entirely; the launcher is reached from each page's "Back to Home".
        sidebar={hideChrome || oneClick ? EmptySidebar : CustomSidebar}
        className={classnames(classes.layout, hideChrome && classes.layoutNoChrome)}
      >
        {/* Recoverable boundary for any page error (e.g. a failed passive reconnect
            refetch). Keyed on pathname so navigating away also clears a stuck error. */}
        {/* One Click's "Back to Home" sits in each page's tab row; a page without
            one gets it on a slim row of its own. The launcher itself needs none. */}
        {hideChrome ? (
          <AppErrorBoundary key={location.pathname}>{props.children}</AppErrorBoundary>
        ) : (
          <BackToHomeProvider>
            <AppErrorBoundary key={location.pathname}>{props.children}</AppErrorBoundary>
          </BackToHomeProvider>
        )}
      </MuiLayout>
    </>
  )
}
export default Layout
